import { useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Typography,
  theme,
  Card,
  Switch,
  Row,
  Col,
  Flex,
  Avatar,
} from 'antd';
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useCreatePaymentLinkMutation, type CreatePaymentLinkRequest } from '../api';
import useAppNotification from '@/hooks/useAppNotification';
import { useTranslation } from 'react-i18next';
import { useModule } from '@/contexts/ModuleContext';
import { sendPaymentNotificationWebhook } from '../services/notificationWebhook';
import { FORM_MODAL_STYLES, formModalWidth } from '@/components/ui/formModalStyles';
import { getApiErrorMessage } from '@/utils/apiError';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

const { Text } = Typography;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Supported currencies for payment links. */
const CURRENCIES = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'JPY', label: 'JPY' },
  { value: 'CAD', label: 'CAD' },
  { value: 'AUD', label: 'AUD' },
  { value: 'BGN', label: 'BGN' },
];

/** Supported payment page locales. */
const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'de', label: 'German' }
];

/** Duration (ms) to show the "Copied!" feedback before reverting. */
const COPY_FEEDBACK_DURATION_MS = 2000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreatePaymentLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Internal form shape — extends the API request with UI-only fields. */
type PaymentFormValues = CreatePaymentLinkRequest & { sendEmail: boolean };

// ---------------------------------------------------------------------------
// Reusable label components
// ---------------------------------------------------------------------------

/** Bold label used for required form fields. */
const FieldLabel = ({ children }: { children: ReactNode }) => (
  <Text strong style={{ fontSize: 12 }}>{children}</Text>
);

/** Label with a secondary "(optional)" suffix for non-required fields. */
const OptionalFieldLabel = ({ children, optionalText }: { children: ReactNode; optionalText: string }) => (
  <Text style={{ fontSize: 12 }}>{children} <Text type="secondary">({optionalText})</Text></Text>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Two-phase modal for creating payment links.
 *
 * **Phase 1 — Form:** collects payment identifiers, amount, customer info,
 * and description. All fields except language are required.
 *
 * **Phase 2 — Success:** displays the generated payment URL with
 * copy-to-clipboard and open-in-new-tab actions.
 *
 * When a notification webhook is configured on the module, an optional toggle
 * lets the user send the payment link to the customer via email.
 */
export function CreatePaymentLinkModal({
  open,
  onClose,
  onSuccess,
}: CreatePaymentLinkModalProps) {
  const { token } = useToken();
  const { t } = useTranslation('payment-module');
  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const [form] = Form.useForm<PaymentFormValues>();
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const { copy, copiedKey } = useCopyToClipboard(COPY_FEEDBACK_DURATION_MS);
  const copied = copiedKey !== null;
  const [emailSent, setEmailSent] = useState(false);
  const createPaymentLink = useCreatePaymentLinkMutation();
  const { config: moduleConfig } = useModule('payment-module');
  const notificationWebhook = moduleConfig?.config?.notificationWebhook;
  const paymentWebhook = moduleConfig?.config?.paymentWebhook;

  /** Whether the modal is showing the success phase (link already created). */
  const isSuccess = paymentUrl !== null;

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /** Resets all local state back to initial values. */
  const resetState = useCallback(() => {
    form.resetFields();
    setPaymentUrl(null);
    setEmailSent(false);
  }, [form]);

  /**
   * Validates the form, creates the payment link via the API, and optionally
   * triggers an email notification webhook.
   */
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const { sendEmail, ...paymentData } = values;
      paymentData.payment_webhook = paymentWebhook ?? null;

      const response = await createPaymentLink.mutateAsync(paymentData);
      setPaymentUrl(response.payment_url);
      showSuccessMessage(t('createModal.createSuccess'));

      // Send email notification if toggled on and a webhook is configured
      if (sendEmail && notificationWebhook) {
        try {
          await sendPaymentNotificationWebhook(notificationWebhook, {
            paymentUrl: response.payment_url,
            requestId: paymentData.request_id,
            amount: paymentData.amount,
            currency: paymentData.currency,
            customerEmail: paymentData.customer_email,
            customerName: paymentData.customer_name,
            description: paymentData.description,
            locale: paymentData.locale,
          });
          setEmailSent(true);
        } catch (webhookError) {
          // Non-blocking: the payment link is still usable even if the notification fails
          console.error('Failed to call notification webhook:', webhookError);
        }
      }
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error, t('createModal.createError'));
      showErrorMessage(errorMessage);
      console.error('Failed to create payment link:', error);
    }
  }, [form, createPaymentLink, notificationWebhook, paymentWebhook, t, showSuccessMessage, showErrorMessage]);

  /** Copies the generated payment URL to the clipboard with brief visual feedback. */
  const handleCopyUrl = useCallback(async () => {
    if (!paymentUrl) return;
    await copy(paymentUrl, {
      successMessage: t('createModal.copySuccess'),
      errorMessage: t('createModal.copyError')
    });
  }, [paymentUrl, copy, t]);

  /** Closes the modal and resets all state. */
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  /** Resets the form so the user can create another link without closing the modal. */
  const handleCreateAnother = useCallback(() => {
    resetState();
    onSuccess?.();
  }, [resetState, onSuccess]);

  // -------------------------------------------------------------------------
  // Memoised styles (depend on the theme token)
  // -------------------------------------------------------------------------

  const successBannerStyle = useMemo(() => ({
    background: token.colorSuccessBg,
    borderRadius: token.borderRadius,
    padding: token.paddingMD,
    marginBottom: token.marginMD,
    textAlign: 'center' as const,
  }), [token]);

  const emailToggleStyle = useMemo(() => ({
    background: token.colorBgLayout,
    borderRadius: token.borderRadius,
    padding: `${token.paddingXS}px ${token.paddingSM}px`,
  }), [token]);

  // -------------------------------------------------------------------------
  // Derived render values
  // -------------------------------------------------------------------------

  const modalTitle = (
    <Flex align="center" gap={token.marginXS}>
      <Avatar
        size={28}
        style={{
          backgroundColor: isSuccess ? token.colorSuccessBg : token.colorPrimaryBg,
          color: isSuccess ? token.colorSuccess : token.colorPrimary,
        }}
        icon={isSuccess ? <CheckCircleOutlined /> : <CreditCardOutlined />}
      />
      <Text strong>{isSuccess ? t('createModal.titleSuccess') : t('createModal.titleCreate')}</Text>
    </Flex>
  );

  const modalFooter = isSuccess
    ? [
        <Button key="another" onClick={handleCreateAnother}>{t('createModal.createAnother')}</Button>,
        <Button key="close" type="primary" onClick={handleClose}>{t('createModal.done')}</Button>,
      ]
    : [
        <Button key="cancel" onClick={handleClose}>{t('createModal.cancel')}</Button>,
        <Button
          key="submit"
          type="primary"
          loading={createPaymentLink.isPending}
          onClick={handleSubmit}
          icon={<CreditCardOutlined />}
        >
          {t('createModal.submit')}
        </Button>,
      ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleClose}
      footer={modalFooter}
      width={formModalWidth(640)}
      styles={FORM_MODAL_STYLES}
      destroyOnHidden
    >
      {isSuccess ? (
        <div>
          {/* Success banner with contextual message */}
          <div style={successBannerStyle}>
            <CheckCircleOutlined
              style={{ fontSize: 32, color: token.colorSuccess, marginBottom: token.marginXS }}
            />
            <div>
              <Text strong style={{ display: 'block' }}>
                {emailSent
                  ? t('createModal.emailSentMessage')
                  : t('createModal.linkReady')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('createModal.copyShareMessage')}
              </Text>
            </div>
          </div>

          {/* Copyable payment URL */}
          <div style={{ marginBottom: token.marginSM }}>
            <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>
              {t('createModal.paymentUrl')}
            </Text>
            <Flex gap={8}>
              <Input
                value={paymentUrl}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <Button
                type="primary"
                icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
                onClick={handleCopyUrl}
              >
                {copied ? t('createModal.copied') : t('createModal.copy')}
              </Button>
            </Flex>
          </div>

          {/* External link to the hosted payment page */}
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => window.open(paymentUrl, '_blank')}
            style={{ padding: 0 }}
            size="small"
          >
            {t('createModal.openInNewTab')}
          </Button>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark
          initialValues={{ locale: 'en', currency: 'EUR', sendEmail: true }}
          size="middle"
        >
          {/* Payment identifiers and amount */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="request_id"
                label={<FieldLabel>{t('createModal.requestId')}</FieldLabel>}
                rules={[
                  { required: true, message: t('createModal.required') },
                  { whitespace: true, message: t('createModal.cannotBeEmpty') },
                ]}
              >
                <Input placeholder={t('createModal.requestIdPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item
                    name="amount"
                    label={<FieldLabel>{t('createModal.amount')}</FieldLabel>}
                    rules={[
                      { required: true, message: t('createModal.required') },
                      { type: 'number', min: 0.01, message: t('createModal.minAmount') },
                    ]}
                  >
                    <InputNumber
                      placeholder={t('createModal.amountPlaceholder')}
                      style={{ width: '100%' }}
                      min={0.01}
                      step={0.01}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="currency"
                    label={<FieldLabel>{t('createModal.currency')}</FieldLabel>}
                    rules={[{ required: true, message: t('createModal.required') }]}
                  >
                    <Select options={CURRENCIES} />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Customer information */}
          <Card
            size="small"
            style={{ marginBottom: token.marginMD, background: token.colorBgLayout }}
            styles={{ body: { padding: token.paddingSM } }}
          >
            <Text type="secondary" style={{ fontSize: 11, marginBottom: token.marginXS, display: 'block' }}>
              {t('createModal.customerDetails')}
            </Text>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer_name"
                  label={<FieldLabel>{t('createModal.name')}</FieldLabel>}
                  rules={[
                    { required: true, message: t('createModal.required') },
                    { whitespace: true, message: t('createModal.cannotBeEmpty') },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t('createModal.namePlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="customer_email"
                  label={<FieldLabel>{t('createModal.email')}</FieldLabel>}
                  rules={[
                    { required: true, message: t('createModal.required') },
                    { type: 'email', message: t('createModal.invalidEmail') },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t('createModal.emailPlaceholder')} type="email" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Description (required) and language preference (optional) */}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="description"
                label={<FieldLabel>{t('createModal.description')}</FieldLabel>}
                rules={[
                  { required: true, message: t('createModal.required') },
                  { whitespace: true, message: t('createModal.cannotBeEmpty') },
                  { max: 50, message: t('createModal.descriptionMaxLength') },
                ]}
              >
                <Input.TextArea
                  placeholder={t('createModal.descriptionPlaceholder')}
                  rows={2}
                  showCount
                  maxLength={50}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="locale"
                label={<OptionalFieldLabel optionalText={t('createModal.optional')}>{t('createModal.language')}</OptionalFieldLabel>}
              >
                <Select options={LOCALES} />
              </Form.Item>
            </Col>
          </Row>

          {/* Email notification toggle — only visible when a webhook is configured */}
          {notificationWebhook && (
            <div style={emailToggleStyle}>
              <Flex justify="space-between" align="center">
                <div>
                  <Text strong style={{ fontSize: 12 }}>{t('createModal.sendToCustomer')}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t('createModal.emailAfterCreation')}
                  </Text>
                </div>
                <Form.Item name="sendEmail" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch />
                </Form.Item>
              </Flex>
            </div>
          )}
        </Form>
      )}
    </Modal>
  );
}
